import { Button, DetailList, formatDateTime, PageEmpty, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Input } from "@majitap/ui/arc/input/input";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import {
  connectionActions,
  formFor,
  INTEGRATION_STATUS,
  type Integration,
  integrationActionsFor,
  integrationQuery,
  KIND,
  type ProviderForm,
  providerFormsQuery,
  quickbooksConnected,
  quickbooksListQuery,
  secretsOf,
  secretsReady,
  typedConfig,
  updateBodyOf,
} from "../../connections";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { ConfirmDialog } from "../shared/confirm";
import { ActivityPanel } from "../shared/panels";
import { IntegrationFields, quickbooksResult } from "./integrations";
import styles from "./settings.module.css";

/** One integration (P8-23): turn it on or off, change its settings and credentials, and its provider's own steps. */
export function IntegrationPage(): ReactNode {
  const { integrationId } = useParams({ from: "/console/settings/integrations/$integrationId" });
  const integration = useQuery(integrationQuery(integrationId));
  const forms = useQuery(providerFormsQuery);
  useRecordCrumb(integration.data?.name);
  if (integration.status === "pending" || forms.status === "pending") return <PageLoading label="Loading" />;
  if (integration.status === "error") {
    const error = errorOf(integration.error);
    if (error.code === "INTEGRATION_NOT_FOUND")
      return <PageEmpty title="No such integration" description="Check the link." />;
    return <PageError message={messageFor(error)} onRetry={() => integration.refetch()} />;
  }
  if (forms.status === "error")
    return <PageError message={messageFor(errorOf(forms.error))} onRetry={() => forms.refetch()} />;
  return <IntegrationView integration={integration.data} form={formFor(forms.data, integration.data)} />;
}

export function IntegrationView({ integration: i, form }: { integration: Integration; form: ProviderForm }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/console/settings/integrations/$integrationId" });
  const can = integrationActionsFor(i);
  const [dialog, setDialog] = useState<"on" | "off" | "disconnect" | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["integrations"] });
  const [label, tone] = INTEGRATION_STATUS[i.status];
  const result = quickbooksResult(search.quickbooks);
  return (
    <>
      <PageHeader
        title={i.name}
        description={`${form.label} · ${KIND[i.kind]}`}
        actions={
          <div className={styles.actions}>
            {can.connect ? <ConnectQuickbooks id={i.id} /> : null}
            {can.disconnect ? (
              <Button variant="secondary" onClick={() => setDialog("disconnect")}>
                Disconnect QuickBooks
              </Button>
            ) : null}
            {i.status === "active" ? (
              <Button variant="secondary" onClick={() => setDialog("off")}>
                Turn off
              </Button>
            ) : (
              <Button onClick={() => setDialog("on")}>Turn on</Button>
            )}
          </div>
        }
      />
      {result ? (
        <Alert tone={result.tone} title={result.tone === "success" ? "Connected" : "Not connected"}>
          {result.text}
        </Alert>
      ) : null}
      {i.status === "error" ? (
        <Alert tone="danger" title="Marked as needing attention">
          MajiTap doesn't use it in this state. Check its settings and credentials, then select Turn on.
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.card}>
            <DetailList
              items={[
                {
                  label: "Status",
                  value: (
                    <span className={styles.actions}>
                      <Badge tone={tone}>{label}</Badge>
                      {i.is_sandbox ? <Badge tone="warning">Test account</Badge> : null}
                    </span>
                  ),
                },
                { label: "Provider", value: form.label },
                {
                  label: "Credentials",
                  value: i.secret_keys.length
                    ? `Stored: ${i.secret_keys
                        .map((k) => form.secrets.find((f) => f.key === k)?.label ?? k.replace(/_/g, " "))
                        .join(", ")}`
                    : "Not set",
                },
                ...(i.provider === "quickbooks"
                  ? [{ label: "QuickBooks", value: quickbooksConnected(i) ? "Connected" : "Not connected" }]
                  : []),
                { label: "Changed", value: formatDateTime(i.updated_at, format) },
              ]}
            />
          </section>
          {can.registerTill ? <RegisterTill /> : null}
          <SettingsForm integration={i} form={form} />
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Activity</h2>
            <ActivityPanel targetType="integration" id={i.id} label="What changed on this integration" />
          </section>
        </div>
      </div>
      {dialog === "on" ? (
        <ConfirmDialog
          title={`Turn on ${i.name}?`}
          description={
            i.kind === "payment"
              ? "Customers start paying through it. If another payment integration is on, check which one the till uses."
              : "MajiTap starts using it straight away."
          }
          action="Turn on"
          run={() => connectionActions.update(i.id, { status: "active" })}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "off" ? (
        <ConfirmDialog
          title={`Turn off ${i.name}?`}
          description="MajiTap stops using it at once: what it does (payments, messages, syncing) stops until it's back on. Its settings stay."
          action="Turn off"
          danger
          run={() => connectionActions.update(i.id, { status: "inactive" })}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "disconnect" ? (
        <ConfirmDialog
          title="Disconnect QuickBooks?"
          description="MajiTap forgets the connection and stops syncing. Mappings stay. Connect again to resume; anything made in between syncs then."
          action="Disconnect"
          danger
          run={() => connectionActions.disconnectQuickbooks(i.id)}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** Sends the browser to Intuit; it comes back to this page with `?quickbooks=`. */
function ConnectQuickbooks({ id }: { id: string }) {
  const connect = useMutation({
    mutationFn: () => connectionActions.connectQuickbooks(id),
    onSuccess: ({ authorize_url }) => window.location.assign(authorize_url),
  });
  return (
    <>
      <Button loading={connect.isPending} onClick={() => connect.mutate()}>
        Connect QuickBooks
      </Button>
      {connect.error ? (
        <Alert tone="danger" title="Couldn't start connecting">
          {messageFor(errorOf(connect.error))}
        </Alert>
      ) : null}
    </>
  );
}

/** Tells Safaricom where to send till payments, and shows the addresses it now uses. */
function RegisterTill() {
  const register = useMutation({ mutationFn: () => connectionActions.registerTillUrls() });
  return (
    <section className={styles.card}>
      <div className={styles.head}>
        <h2 className={styles.sectionTitle}>Till payments</h2>
        <Button variant="secondary" size="sm" loading={register.isPending} onClick={() => register.mutate()}>
          Register till URLs
        </Button>
      </div>
      <p className={styles.muted}>
        Do this once after turning M-Pesa on, and again after the shortcode changes: until then, payments customers make
        to the till don't reach MajiTap.
      </p>
      {register.data ? (
        <Alert tone="success" title="Registered with Safaricom">
          Payments are confirmed at {register.data.confirmation_url} and checked at {register.data.validation_url}.
        </Alert>
      ) : null}
      {register.error ? (
        <Alert tone="danger" title="Not registered">
          {messageFor(errorOf(register.error))}
        </Alert>
      ) : null}
    </section>
  );
}

/** The settings and credentials. Credentials go only when typed, and then all of them. */
/**
 * A connected QuickBooks company's accounts, tax codes and items, so its settings are picked rather
 * than typed. A list that fails stays typed (undefined).
 */
function useQuickbooksPicks(i: Integration) {
  const on = quickbooksConnected(i);
  const accounts = useQuery({ ...quickbooksListQuery(i.id, "accounts"), enabled: on });
  const taxCodes = useQuery({ ...quickbooksListQuery(i.id, "tax_codes"), enabled: on });
  const items = useQuery({ ...quickbooksListQuery(i.id, "items"), enabled: on });
  return on ? { accounts: accounts.data, tax_codes: taxCodes.data, items: items.data } : undefined;
}

export function SettingsForm({ integration: i, form }: { integration: Integration; form: ProviderForm }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(i.name);
  const [sandbox, setSandbox] = useState(i.is_sandbox);
  const [config, setConfig] = useState(() => typedConfig(form.config, i.config));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const typedSecrets = secretsOf(form.secrets, secrets);
  const save = useMutation({
    mutationFn: () => connectionActions.update(i.id, updateBodyOf(i, form, { name, sandbox, config, secrets })),
    onSuccess: async () => {
      setSecrets({});
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
  const missing = form.config.some((f) => f.required && !(config[f.key] ?? "").trim());
  const picks = useQuickbooksPicks(i);
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Settings</h2>
      <div className={styles.form}>
        <div className={styles.pair}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <Switch label="A test (sandbox) account" checked={sandbox} onCheckedChange={setSandbox} />
        <IntegrationFields
          form={form}
          picks={picks}
          config={config}
          secrets={secrets}
          onConfig={(next) => {
            setSaved(false);
            setConfig(next);
          }}
          onSecrets={(next) => {
            setSaved(false);
            setSecrets(next);
          }}
          stored={i.secret_keys}
        />
        {typedSecrets && quickbooksConnected(i) ? (
          <Alert tone="warning" title="This disconnects QuickBooks">
            New credentials replace the stored connection too. Connect QuickBooks again after saving, or syncing stops.
          </Alert>
        ) : null}
        {!secretsReady(form.secrets, secrets) ? (
          <p className={styles.danger}>Type every required credential: saving replaces them all.</p>
        ) : null}
        {save.error ? (
          <Alert tone="danger" title="Not saved">
            {messageFor(errorOf(save.error))}
          </Alert>
        ) : null}
        {saved ? (
          <Alert tone="success" title="Saved">
            {i.status === "active" ? "MajiTap uses the new settings from now on." : "Turn it on when it's ready."}
          </Alert>
        ) : null}
        <div>
          <Button
            disabled={!name.trim() || missing || !secretsReady(form.secrets, secrets)}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save the settings
          </Button>
        </div>
      </div>
    </section>
  );
}
